import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { useId } from 'react';

import { cn } from '@/lib/cn';

/** Shared by Field, Select and Textarea so the three line up exactly. */
export const CONTROL_CLASS = cn(
  'w-full rounded-md border bg-raised px-3 text-ui text-ink transition-colors duration-150',
  'placeholder:text-subtle',
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-subtle',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:bg-danger-soft/40',
);

export interface FieldShellProps {
  label: ReactNode;
  /** Sits under the control when there is no error. */
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  /** Receives the ids it must be wired to. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/**
 * Label, control, message - wired together.
 *
 * The render-prop exists so every control in the app gets the same `id`,
 * `aria-describedby` and `aria-invalid` wiring without each one re-deriving
 * it. Forgetting `aria-describedby` is the most common way an error message
 * ends up visible to sighted users and silent to everyone else.
 */
export function FieldShell({
  label,
  hint,
  error,
  required = false,
  className,
  children,
}: FieldShellProps) {
  const id = useId();
  const messageId = `${id}-message`;
  const invalid = Boolean(error);
  const message = error ?? hint;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={id}
        className="text-nano font-semibold uppercase tracking-[0.1em] text-muted"
      >
        {label}
        {required ? (
          <span className="ml-1 text-accent" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children({ id, describedBy: message ? messageId : undefined, invalid })}

      {message ? (
        <p
          id={messageId}
          role={error ? 'alert' : undefined}
          className={cn('text-small', error ? 'font-medium text-danger-ink' : 'text-subtle')}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  /** Renders in the input's own font, e.g. mono for a six-digit code. */
  mono?: boolean;
  /** React 19 passes `ref` as an ordinary prop; declare it so callers may. */
  ref?: Ref<HTMLInputElement>;
  wrapperClassName?: string;
}

export function Field({
  label,
  hint,
  error,
  mono = false,
  required,
  className,
  wrapperClassName,
  ref,
  ...rest
}: FieldProps) {
  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          ref={ref}
          required={required}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className={cn(
            CONTROL_CLASS,
            'h-10',
            mono && 'font-mono tracking-[0.2em]',
            invalid ? 'border-danger' : 'border-line-strong hover:border-ink/25',
            className,
          )}
          {...rest}
        />
      )}
    </FieldShell>
  );
}
